import { useSelector } from 'react-redux';
import { getUserWallet, getSelectedProduct } from '@/selectors/walletSelector';
import {
  autoOpenedStatusSessionKey,
  Card,
  redirectedSessionKey,
} from '@/constants';
import { getIsLoggedIn } from '@/selectors/authSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  isActiveAccount,
  isActiveCenturionAccount,
} from '@/helpers/accountHelper';

// This function will return the account token of the first active Centurion account or the first active account
export const useCenturionOrActiveToken = () => {
  const wallet = useSelector(getUserWallet);
  const centurionAccountToken = wallet?.find(
    isActiveCenturionAccount
  )?.accountToken;
  const activeAccountToken = wallet?.find(isActiveAccount)?.accountToken;
  return centurionAccountToken || activeAccountToken;
};

export const useUserCardsWithLoungeAccess = () => {
  const wallet = useSelector(getUserWallet);
  const cardsWithLoungeAccess = wallet?.filter(
    (acct) => acct.hasLoungeBenefit && acct.active
  );
  return cardsWithLoungeAccess;
};

/**
 * Returns the selected product from the user's wallet if there's a match.
 *
 * @param {string} product Lounge Finder selected product (from the URL).
 * @param {string} locale Lounge Finder selected locale (from the Redux store).
 * @returns {object} Product that matches the product type and locale.
 */
export const useSelectedProductFromUserWallet = (product, locale = '') => {
  const selectedProduct = useSelector(getSelectedProduct);
  const matchedProductType =
    selectedProduct?.loungeFinderProduct === product &&
    locale?.toLocaleLowerCase() ===
      selectedProduct?.homeCountryLocale?.toLocaleLowerCase();

  if (matchedProductType) {
    return selectedProduct;
  }

  return null;
};

export const useCenturionProductFromUserWallet = () => {
  const wallet = useSelector(getUserWallet);
  const centurionProduct = wallet?.find(
    (acct) => acct?.loungeFinderProduct === Card.Centurion
  );

  return centurionProduct;
};

export const useAutoOpenCardPickerStatus = () => {
  const { product, locale } = useRouteParams();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const eligibleCards = useUserCardsWithLoungeAccess();
  const wallet = useSelector(getUserWallet);

  if (typeof window === 'undefined' || !isLoggedIn) {
    return {
      reasons: [],
      shouldShowCardPicker: false,
    };
  }

  const userHasEligibleCards = eligibleCards?.length > 0;
  const activeProductWasFoundInUserWallet = wallet?.find(
    (card) =>
      card.loungeFinderProduct === product &&
      card.homeCountryLocale?.toLocaleLowerCase() ===
        locale?.toLocaleLowerCase()
  );
  const cardPickerHasBeenAutoOpened =
    window.sessionStorage.getItem(autoOpenedStatusSessionKey) === '1';
  // if user was redirected to their best product we don't want to open the card picker
  const userWasRedirected =
    window.sessionStorage.getItem(redirectedSessionKey) === '1';

  let shouldShowCardPicker = false;
  if (!cardPickerHasBeenAutoOpened && !userWasRedirected) {
    shouldShowCardPicker =
      !userHasEligibleCards || activeProductWasFoundInUserWallet === undefined;
  }

  const reasons = [];

  if (!userHasEligibleCards) {
    reasons.push('CM has no cards with lounge access');
  }

  if (!activeProductWasFoundInUserWallet) {
    reasons.push("CM's wallet does not contain the selected product");
  }

  return {
    reasons,
    shouldShowCardPicker,
  };
};
